import {Formik, Field} from 'formik';
import {object, string, bool} from 'yup';
import {FormikTextInput, FormikPhoneInput, FormikCheckbox, FormikSelect, FormikDateTimePicker, Card} from 'shipwell-ui';
import FormFooter from 'App/formComponents/formSections/formFooter';
import {validatePhoneNumber, validateDollarValue} from 'App/components/forms/utils';
import {states, removeCommasAndDollarSign, countDecimals} from 'App/utils/globals';
import {countries} from 'App/utils/countries';
import './styles.scss';

/** Default UPS Registration values */
export const defaultValues = {
  account_number: '',
  invoice_amount: null,
  invoice_currency: null,
  invoice_date: null,
  invoice_number: null,
  invoice_control_id: null,
  address: {
    line_1: '',
    city: '',
    state_or_province: '',
    postal_code: '',
    country_code: ''
  },
  name: '',
  company: '',
  email: '',
  phone: '',
  accept_terms: false
};

/** UPS Registration validation rules */
export const validationSchema = object().shape({
  account_number: string().required('Account number is required.'),
  invoice_amount: string()
    .test('dollar_value', 'Enter a valid dollar amount.', (value) => {
      return value ? validateDollarValue(value) && countDecimals(value) === 2 : true;
    })
    .nullable(),
  invoice_currency: string().nullable(),
  invoice_date: string().nullable(),
  invoice_number: string().nullable(),
  invoice_control_id: string().nullable(),
  address: object().shape({
    line_1: string().required('Street address is required.'),
    city: string().required('City is required.'),
    state_or_province: string().required('State is required.'),
    postal_code: string().required('Postal code is required.'),
    country_code: string().required('Country is required.')
  }),
  name: string().required('Name is required.'),
  company: string().required('Company is required.'),
  email: string().email('Enter a valid email.').required('Email is required.'),
  phone: string()
    .test('valid-phone', 'Enter a valid phone number.', (value) => {
      return validatePhoneNumber(value);
    })
    .required('Phone number is required'),
  accept_terms: bool().oneOf([true], 'Must accept the UPS License Agreement.')
});

/**
 * UPS Account Registration Form
 * @param {*} props
 */
const UPSRegistrationForm = (props) => {
  const {values, defaultValues, validationSchema, onCancel, onSubmit, ...formProps} = props;

  /** Form cancel callback  */
  const handleCancel = (values, actions) => {
    if (onCancel) {
      onCancel(values, actions);
    }
  };

  /** Form submit callback  */
  const handleSubmit = (values, actions) => {
    if (onSubmit) {
      onSubmit(values, actions);
    }
  };

  const handleNormalizedChange = (handleChange) => (e) => {
    e.target.value = removeCommasAndDollarSign(e.target.value);
    return handleChange(e);
  };

  return (
    <Formik
      {...formProps}
      enableReinitialize
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      initialValues={{...defaultValues, ...values}}
    >
      {({handleSubmit, handleChange, isSubmitting, isValid, dirty}) => (
        <form className="ups-registration__form" noValidate="novalidate" onSubmit={handleSubmit}>
          <Card title="Account">
            <div className="ups-registration__form-section account-number">
              <div className="ups-registration__section-title">
                <b>Account Number</b>
              </div>
              <div className="field-grid">
                <Field required name="account_number" label="UPS Account Number" component={FormikTextInput} />
              </div>
            </div>
            <div className="ups-registration__form-section">
              <div className="ups-registration__section-title">
                <b>Invoice Information</b>
                <p>
                  If you have received an invoice from UPS in the last 90 days then you will need to fill out all of the
                  fields below with the details fron the UPS invoice.
                </p>
              </div>
              <div className="field-grid">
                <Field
                  name="invoice_amount"
                  label="Invoice Amount"
                  prepend="$"
                  onChange={handleNormalizedChange(handleChange)}
                  component={FormikTextInput}
                />
                <Field
                  simpleValue
                  name="invoice_currency"
                  label="Currency"
                  options={[
                    {label: 'USD', value: 'USD'},
                    {label: 'CAD', value: 'CAD'}
                  ]}
                  component={FormikSelect}
                />
                <Field
                  name="invoice_date"
                  label="Invoice Date"
                  showTimeSelect={false}
                  component={FormikDateTimePicker}
                />
                <Field name="invoice_number" label="Invoice Number" component={FormikTextInput} />
                <Field name="invoice_control_id" label="Invoice Control ID" component={FormikTextInput} />
              </div>
            </div>
          </Card>
          <Card title="Contact Information">
            <div className="field-grid">
              <Field required name="name" label="First and Last Name" component={FormikTextInput} />
              <Field required name="company" label="Company Name" component={FormikTextInput} />
              <Field required name="email" label="Email Address" component={FormikTextInput} />
              <Field required name="phone" label="Phone Number" component={FormikPhoneInput} />
            </div>
          </Card>
          <Card title="Billing Address">
            <div className="field-grid">
              <div className="grid-item-2">
                <Field required name="address.line_1" label="Street Address" component={FormikTextInput} />
              </div>
              <Field required name="address.city" label="City" component={FormikTextInput} />
              <Field
                simpleValue
                required
                clearable={false}
                name="address.state_or_province"
                label="State"
                options={states.map((e) => ({label: e.name, value: e.id}))}
                component={FormikSelect}
              />
              <Field required name="address.postal_code" label="Postal Code" component={FormikTextInput} />
              <Field
                simpleValue
                required
                clearable={false}
                name="address.country_code"
                label="Country"
                options={countries}
                component={FormikSelect}
              />
            </div>
          </Card>
          <div className="grid-checkbox-field ups-registration__terms-checkbox">
            <Field
              name="accept_terms"
              label={
                <span>
                  I accept the{' '}
                  <a href="https://www.ups.com/assets/resources/media/en_US/UTA.pdf" target="_blank" rel="noreferrer">
                    UPS License Agreement
                  </a>
                </span>
              }
              component={FormikCheckbox}
            />
          </div>
          {dirty && (
            <FormFooter
              primaryActionName="Connect UPS"
              isValid={isValid}
              isSubmitting={isSubmitting}
              onCancel={handleCancel}
            />
          )}
        </form>
      )}
    </Formik>
  );
};

UPSRegistrationForm.defaultProps = {
  defaultValues,
  validationSchema
};

export default UPSRegistrationForm;
